import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, WafChallengeError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_SIGNUP_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, WafChallengeNotice, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/**
 * `a-signup` — process/09-SCREEN-SPECS/a-signup.md, design/03-auth-and-credentials.md §3.1i.
 * "One call, four fields, one form" (`identitySignUp`). Mints the organisation unless an
 * invitation is present; returns no tokens, so this always hands off to `a-confirm`.
 */
export function ASignup({
  invitation,
  onSignedUp,
  onGoToSignIn,
}: {
  invitation?: string
  onSignedUp: (email: string, invitation?: string) => void
  onGoToSignIn: () => void
}) {
  const [orgName, setOrgName] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const signUp = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error, response } = await client.POST('/auth/sign-up', {
        body: { organisation_name: orgName, display_name: name, email, password, invitation },
      })
      if (error) {
        if (response.status === 405) throw new WafChallengeError()
        throw new ApiProblemError(error as Problem)
      }
    },
    onSuccess: () => onSignedUp(email, invitation),
  })

  const problem = signUp.error instanceof ApiProblemError ? signUp.error.problem : undefined
  const wafChallenge = signUp.error instanceof WafChallengeError
  const mismatch = password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword

  return (
    <AuthCard unitId={unitId('a-signup')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_SIGNUP_COPY.heading}</h3>
      <AuthCardLead>{A_SIGNUP_COPY.lead}</AuthCardLead>

      <FieldStack>
        <Field label={A_SIGNUP_COPY.orgNameLabel}>
          <input className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={orgName} onChange={(e) => setOrgName(e.target.value)} />
        </Field>
        <Field label={A_SIGNUP_COPY.nameLabel}>
          <input className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={A_SIGNUP_COPY.emailLabel}>
          <input type="email" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={A_SIGNUP_COPY.passwordLabel}>
          <input type="password" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label={A_SIGNUP_COPY.confirmPasswordLabel}>
          <input
            type="password"
            className={TEXT_INPUT_CLASSNAME}
            style={TEXT_INPUT_STYLE}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Field>
      </FieldStack>

      <Note>{A_SIGNUP_COPY.passwordRule}</Note>
      <Note>{A_SIGNUP_COPY.orgNameNote}</Note>

      {mismatch && <FormError>{A_SIGNUP_COPY.passwordMismatch}</FormError>}
      {wafChallenge && <WafChallengeNotice />}
      {problem && <ASignupRefusal problem={problem} />}

      <Button
        data-act={actionId('session.signUp')}
        full
        disabled={signUp.isPending || mismatch || !orgName || !name || !email || !password || !confirmPassword}
        onClick={() => signUp.mutate()}
      >
        {signUp.isPending ? 'Creating…' : A_SIGNUP_COPY.submitButton}
      </Button>

      <Note>
        {A_SIGNUP_COPY.alreadyHaveAccountPrefix}
        <a href="#" data-act={actionId('nav.a-signin')} onClick={(e) => { e.preventDefault(); onGoToSignIn() }} className="text-link">
          {A_SIGNUP_COPY.signInLinkLabel}
        </a>
        .
      </Note>
      <Note>{A_SIGNUP_COPY.mfaNote}</Note>
    </AuthCard>
  )
}

function ASignupRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'VALIDATION_FAILED') {
    return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
  }
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  if (isUnexpectedRefusal(problem.refusal_kind)) return <UnexpectedRefusal problem={problem} />
  return <FormError>{problem.title}</FormError>
}
